import React, { useState } from 'react';
import { History } from 'lucide-react';
import { useDeleteExpense } from '../../hooks/useExpenses';
import type { Expense } from '../../types';
import { AttachmentViewer } from './AttachmentViewer';
import { ConfirmModal } from '../common/ConfirmModal';
import AuditDrawer from '../audit/AuditDrawer';

interface ExpenseListProps {
  expenses?: Expense[];
  isLoading?: boolean;
  onEdit?: (expense: Expense) => void;
}

const ExpenseList: React.FC<ExpenseListProps> = ({ expenses = [], isLoading = false, onEdit }) => {
  const deleteExpense = useDeleteExpense();
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [viewingAttachments, setViewingAttachments] = useState<{
    isOpen: boolean;
    expenseId: string;
    attachmentIds: string[];
  }>({
    isOpen: false,
    expenseId: '',
    attachmentIds: [],
  });
  const [confirmDelete, setConfirmDelete] = useState<{
    isOpen: boolean;
    expenseId: string;
    provider: string;
  }>({
    isOpen: false,
    expenseId: '',
    provider: '',
  });
  const [auditDrawer, setAuditDrawer] = useState<{
    isOpen: boolean;
    expenseId: string;
    provider: string;
  }>({
    isOpen: false,
    expenseId: '',
    provider: '',
  });
  
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
    }).format(amount);
  };
  
  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('es-CL', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };
  
  const handleDelete = async (expenseId: string) => {
    setDeletingId(expenseId);
    try {
      await deleteExpense.mutateAsync(expenseId);
      setConfirmDelete({ isOpen: false, expenseId: '', provider: '' });
    } catch (error) {
      console.error('Error deleting expense:', error);
    } finally {
      setDeletingId(null);
    }
  };

  const handleDeleteClick = (expense: Expense) => {
    setConfirmDelete({
      isOpen: true,
      expenseId: expense.expenseId,
      provider: expense.provider,
    });
  };

  const handleViewAttachments = (expense: Expense) => {
    setViewingAttachments({
      isOpen: true,
      expenseId: expense.expenseId,
      attachmentIds: expense.attachmentIds || [],
    });
  };

  const handleCloseAttachments = () => {
    setViewingAttachments({
      isOpen: false,
      expenseId: '',
      attachmentIds: [],
    });
  };

  const handleViewHistory = (expense: Expense) => {
    setAuditDrawer({
      isOpen: true,
      expenseId: expense.expenseId,
      provider: expense.provider,
    });
  };

  const handleCloseHistory = () => {
    setAuditDrawer({
      isOpen: false,
      expenseId: '',
      provider: '',
    });
  };
  
  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  if (!expenses || expenses.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No hay gastos registrados
      </div>
    );
  }
  
  return (
    <>
      {/* Confirm Delete Modal */}
      <ConfirmModal
        isOpen={confirmDelete.isOpen}
        title="Eliminar Gasto"
        message={`¿Estás seguro de que deseas eliminar el gasto de "${confirmDelete.provider}"?\n\nEsta acción no se puede deshacer.`}
        confirmText="Eliminar"
        confirmColor="red"
        onConfirm={() => handleDelete(confirmDelete.expenseId)}
        onCancel={() => setConfirmDelete({ isOpen: false, expenseId: '', provider: '' })}
      />

      {/* Attachment Viewer Modal */}
      <AttachmentViewer
        isOpen={viewingAttachments.isOpen}
        expenseId={viewingAttachments.expenseId}
        attachmentIds={viewingAttachments.attachmentIds}
        onClose={handleCloseAttachments}
        readOnly={false}
      />

      {/* Desktop table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Fecha
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Proyecto
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Proveedor
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Nº Doc
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Categoría
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Subcategoría
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Activos
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Monto
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Descripción
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {expenses.map((expense) => (
              <tr key={expense.expenseId} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                  {formatDate(expense.date)}
                </td>
                <td className="px-4 py-3 text-sm text-gray-900">
                  {expense.projectName || 'Sin proyecto'}
                </td>
                <td className="px-4 py-3 text-sm text-gray-900">
                  {expense.provider}
                </td>
                <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                  {expense.documentNumber || <span className="text-gray-400">-</span>}
                </td>
                <td className="px-4 py-3 text-sm text-gray-900">
                  {expense.categoryName}
                </td>
                <td className="px-4 py-3 text-sm text-gray-900">
                  {expense.subcategoryName}
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">
                  {expense.assets && expense.assets.length > 0 ? (
                    <div className="space-y-1">
                      {expense.assets.map((asset, index) => (
                        <div key={index} className="text-xs">
                          <span className="font-medium">{asset.displayName}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
                <td className="px-4 py-3 text-sm text-gray-900 text-right font-medium">
                  {formatCurrency(expense.amount)}
                </td>
                <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate">
                  {expense.description}
                </td>
                <td className="px-4 py-3 text-sm text-center whitespace-nowrap">
                  <div className="flex items-center justify-center gap-2">
                    {/* View attachments button - always reserve space */}
                    <div className="w-5 h-5 flex items-center justify-center">
                      {expense.attachmentIds && expense.attachmentIds.length > 0 && (
                        <button
                          onClick={() => handleViewAttachments(expense)}
                          className="text-gray-600 hover:text-gray-800 relative"
                          title="Ver adjuntos"
                        >
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
                            />
                          </svg>
                          <span className="absolute -top-1 -right-1 bg-blue-600 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                            {expense.attachmentIds.length}
                          </span>
                        </button>
                      )}
                    </div>
                    {/* History button */}
                    <button
                      onClick={() => handleViewHistory(expense)}
                      className="text-gray-600 hover:text-gray-800 w-5 h-5 flex items-center justify-center"
                      title="Ver historial"
                    >
                      <History className="w-5 h-5" />
                    </button>
                    {onEdit && (
                      <button
                        onClick={() => onEdit(expense)}
                        className="text-blue-600 hover:text-blue-800 w-5 h-5 flex items-center justify-center"
                        title="Editar"
                      >
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                          />
                        </svg>
                      </button>
                    )}
                    <button
                      onClick={() => handleDeleteClick(expense)}
                      disabled={deletingId === expense.expenseId}
                      className="text-red-600 hover:text-red-800 disabled:opacity-50 w-5 h-5 flex items-center justify-center"
                      title="Eliminar"
                    >
                      {deletingId === expense.expenseId ? (
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-red-600"></div>
                      ) : (
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                      )}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {/* Mobile cards */}
      <div className="md:hidden space-y-4">
        {expenses.map((expense) => (
          <div
            key={expense.expenseId}
            className="bg-white p-4 rounded-lg shadow border border-gray-200"
          >
            <div className="flex justify-between items-start mb-3">
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900">{expense.provider}</h3>
                <p className="text-sm text-gray-500">{formatDate(expense.date)}</p>
              </div>
              <div className="text-right">
                <div className="text-lg font-bold text-gray-900">
                  {formatCurrency(expense.amount)}
                </div>
              </div>
            </div>
            
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Proyecto:</span>
                <span className="text-gray-900 font-medium">{expense.projectName || 'Sin proyecto'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Categoría:</span>
                <span className="text-gray-900 font-medium">{expense.categoryName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Subcategoría:</span>
                <span className="text-gray-900 font-medium">{expense.subcategoryName}</span>
              </div>
              {expense.documentNumber && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Nº Doc:</span>
                  <span className="text-gray-900 font-medium">{expense.documentNumber}</span>
                </div>
              )}
              {expense.assets && expense.assets.length > 0 && (
                <div>
                  <span className="text-gray-500">Activos:</span>
                  <div className="mt-1 space-y-1">
                    {expense.assets.map((asset, index) => (
                      <div key={index} className="text-gray-900 font-medium text-xs">
                        {asset.displayName}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <span className="text-gray-500">Descripción:</span>
                <p className="text-gray-900 mt-1">{expense.description}</p>
              </div>
            </div>
            
            <div className="mt-4 flex flex-wrap gap-2">
              {expense.attachmentIds && expense.attachmentIds.length > 0 && (
                <button
                  onClick={() => handleViewAttachments(expense)}
                  className="flex-1 px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 text-sm font-medium flex items-center justify-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                  </svg>
                  Adjuntos ({expense.attachmentIds.length})
                </button>
              )}
              <button
                onClick={() => handleViewHistory(expense)}
                className="flex-1 px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 text-sm font-medium flex items-center justify-center gap-2"
              >
                <History className="w-4 h-4" />
                Historial
              </button>
              {onEdit && (
                <button
                  onClick={() => onEdit(expense)}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium"
                >
                  Editar
                </button>
              )}
              <button
                onClick={() => handleDeleteClick(expense)}
                disabled={deletingId === expense.expenseId}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm font-medium disabled:opacity-50"
              >
                {deletingId === expense.expenseId ? 'Eliminando...' : 'Eliminar'}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Audit Drawer */}
      <AuditDrawer
        entityType="EXPENSE"
        entityId={auditDrawer.expenseId}
        entityName={auditDrawer.provider}
        isOpen={auditDrawer.isOpen}
        onClose={handleCloseHistory}
      />
    </>
  );
};

export default ExpenseList;
